import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Avatar,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Badge,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  InventoryOutlined,
  PsychologyOutlined,
  CalculateOutlined,
  PersonAddOutlined,
  WarningAmberOutlined,
  InfoOutlined,
  CheckCircleOutline,
  MarkEmailRead,
  Clear,
  DoneAll,
  FilterList,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/notifications/');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setError('');
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch('/api/notifications/', { notification_id: notificationId });
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch('/api/notifications/');
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconProps = { sx: { fontSize: 24 } };
    switch (type) {
      case 'inventory_expiring':
        return <InventoryOutlined {...iconProps} color="warning" />;
      case 'new_prediction':
        return <PsychologyOutlined {...iconProps} color="primary" />;
      case 'cost_calculated':
        return <CalculateOutlined {...iconProps} color="success" />;
      case 'user_registered':
        return <PersonAddOutlined {...iconProps} color="info" />;
      case 'system_alert':
        return <WarningAmberOutlined {...iconProps} color="error" />;
      default:
        return <InfoOutlined {...iconProps} color="info" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'inventory_expiring':
        return 'warning';
      case 'new_prediction':
        return 'primary';
      case 'cost_calculated':
        return 'success';
      case 'user_registered':
        return 'info';
      case 'system_alert':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    if (filter !== 'all') return notif.type === filter;
    return true;
  });

  const notificationTypes = [
    { value: 'all', label: 'All', count: notifications.length },
    { value: 'unread', label: 'Unread', count: unreadCount },
    { value: 'inventory_expiring', label: 'Inventory', count: notifications.filter(n => n.type === 'inventory_expiring').length },
    { value: 'new_prediction', label: 'Predictions', count: notifications.filter(n => n.type === 'new_prediction').length },
    { value: 'cost_calculated', label: 'Costs', count: notifications.filter(n => n.type === 'cost_calculated').length },
    { value: 'system_alert', label: 'System', count: notifications.filter(n => n.type === 'system_alert').length },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon sx={{ fontSize: 'inherit', color: '#1976d2' }} />
            </Badge>
            Notifications
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Tooltip title="Refresh notifications">
              <IconButton onClick={fetchNotifications} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
            
            {unreadCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<DoneAll />}
                onClick={markAllAsRead}
                size="small"
              >
                Mark All Read
              </Button>
            )}
          </Box>
        </Box>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </motion.div>
      )}

      <Grid container spacing={3}>
        {/* Filter Sidebar */}
        <Grid item xs={12} md={3}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Card className="card-hover">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterList />
                  Filters
                </Typography>
                
                <List dense>
                  {notificationTypes.map((type) => (
                    <ListItem
                      key={type.value}
                      button
                      selected={filter === type.value}
                      onClick={() => setFilter(type.value)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          }
                        }
                      }}
                    >
                      <ListItemText primary={type.label} />
                      <Chip
                        label={type.count}
                        size="small"
                        color={filter === type.value ? 'secondary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Notifications List */}
        <Grid item xs={12} md={9}>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {filteredNotifications.length === 0 ? (
              <Card sx={{ textAlign: 'center', py: 6 }}>
                <CardContent>
                  <NotificationsIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No notifications found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filter === 'unread' 
                      ? 'All caught up! No unread notifications.'
                      : 'Check back later for new notifications.'
                    }
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-hover">
                <CardContent sx={{ p: 0 }}>
                  <List sx={{ py: 0 }}>
                    {filteredNotifications.map((notification, index) => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                            borderLeft: notification.is_read ? 'none' : '4px solid',
                            borderLeftColor: `${getNotificationColor(notification.type)}.main`,
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: `${getNotificationColor(notification.type)}.light`,
                                color: `${getNotificationColor(notification.type)}.main`,
                              }}
                            >
                              {getNotificationIcon(notification.type)}
                            </Avatar>
                          </ListItemIcon>
                          
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: notification.is_read ? 400 : 600,
                                    color: notification.is_read ? 'text.secondary' : 'text.primary'
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatTimeAgo(notification.created_at)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  {notification.message}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={notification.type.replace('_', ' ').toUpperCase()}
                                    size="small"
                                    color={getNotificationColor(notification.type)}
                                    variant="outlined"
                                  />
                                  {!notification.is_read && (
                                    <Chip
                                      label="NEW"
                                      size="small"
                                      color="error"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          
                          {!notification.is_read && (
                            <Tooltip title="Mark as read">
                              <IconButton
                                onClick={() => markAsRead(notification.id)}
                                size="small"
                                color="primary"
                              >
                                <MarkEmailRead />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItem>
                        
                        {index < filteredNotifications.length - 1 && (
                          <Divider sx={{ mx: 3 }} />
                        )}
                      </motion.div>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="refresh"
        onClick={fetchNotifications}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
        }}
      >
        <Refresh />
      </Fab>
    </Box>
  );
};

export default Notifications; 